/**
 * Performance benchmarking instrumentation utilities
 *
 * Provides centralized performance markers and timing instrumentation
 * for OpenAI API operations and UI interactions.
 */
import { getBenchmarkInstrumentationEnabled } from './envHelpers';
/**
 * Centralized performance instrumentation class
 */
export class BenchmarkInstrumentation {
    constructor() {
        this.timings = new Map();
        // Don't cache the enabled state to allow dynamic enabling via Playwright
    }
    /**
     * Get singleton instance
     */
    static getInstance() {
        if (!BenchmarkInstrumentation.instance) {
            BenchmarkInstrumentation.instance = new BenchmarkInstrumentation();
        }
        return BenchmarkInstrumentation.instance;
    }
    /**
     * Check if instrumentation is enabled (dynamically checks each time)
     */
    isInstrumentationEnabled() {
        return getBenchmarkInstrumentationEnabled();
    }
    /**
     * Refresh enabled state from environment helper to allow dynamic toggling
     * @deprecated This method is no longer needed as we check dynamically
     */
    refreshEnabledState() {
        // No-op: we now check dynamically in isInstrumentationEnabled()
    }
    /**
     * Set a performance marker
     */
    setMarker(name, timestamp) {
        if (!this.isInstrumentationEnabled())
            return;
        const ts = timestamp || performance.now();
        // Set marker in browser for compatibility with existing benchmark system
        if (typeof window !== 'undefined' && window.setBenchmarkMarker) {
            window.setBenchmarkMarker(name, ts);
        }
        // Also log for debugging
        console.log(`[BENCHMARK] ${name}: ${ts.toFixed(2)}ms`);
    }
    /**
     * Start timing an operation
     */
    startTiming(operation, metadata) {
        if (!this.isInstrumentationEnabled())
            return;
        const timing = {
            operation,
            startTime: performance.now(),
            metadata,
        };
        this.timings.set(operation, timing);
        this.setMarker(`${operation}_start`);
    }
    /**
     * End timing an operation
     */
    endTiming(operation, metadata) {
        if (!this.isInstrumentationEnabled())
            return null;
        const timing = this.timings.get(operation);
        if (!timing) {
            console.warn(`[BENCHMARK] No start timing found for operation: ${operation}`);
            return null;
        }
        const endTime = performance.now();
        const duration = endTime - timing.startTime;
        timing.endTime = endTime;
        timing.duration = duration;
        if (metadata) {
            timing.metadata = { ...timing.metadata, ...metadata };
        }
        this.setMarker(`${operation}_end`);
        console.log(`[BENCHMARK] ${operation} completed in ${duration.toFixed(2)}ms`);
        return duration;
    }
    /**
     * Get timing data for an operation
     */
    getTiming(operation) {
        return this.timings.get(operation) || null;
    }
    /**
     * Get all timing data
     */
    getAllTimings() {
        return Array.from(this.timings.values());
    }
    /**
     * Clear all timing data
     */
    clearTimings() {
        this.timings.clear();
    }
    /**
     * Wrap an async operation with timing
     */
    async timeOperation(operation, fn, metadata) {
        if (!this.isInstrumentationEnabled()) {
            return fn();
        }
        this.startTiming(operation, metadata);
        try {
            const result = await fn();
            this.endTiming(operation, { success: true });
            return result;
        }
        catch (error) {
            this.endTiming(operation, {
                success: false,
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    }
}
BenchmarkInstrumentation.instance = null;
/**
 * Global instrumentation instance
 */
export const benchmarkInstrumentation = BenchmarkInstrumentation.getInstance();
/**
 * Convenience functions for common operations
 */
export const setMarker = (name, timestamp) => benchmarkInstrumentation.setMarker(name, timestamp);
export const startTiming = (operation, metadata) => benchmarkInstrumentation.startTiming(operation, metadata);
export const endTiming = (operation, metadata) => benchmarkInstrumentation.endTiming(operation, metadata);
export const timeOperation = (operation, fn, metadata) => benchmarkInstrumentation.timeOperation(operation, fn, metadata);
